import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { DropdownItem, Flex, Text } from '@tremor/react';
import { FC, useEffect, useMemo, useState } from 'react';
import s from './UsersTable.module.scss';
import { isUserRole } from '@utils/typeGuards';
import { FetcherEmptyResponse, UserRole, userRoles } from '@/types/types';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { Checkbox } from '@/components/layouts/Inputs/Checkbox/Checkbox';
import { OutlinedSelectInput } from '@/components/layouts/Inputs/OutlinedSelectInput/OutlinedSelectInput';
import { RemoveUserButton } from '@/components/layouts/Buttons/RemoveUserButton/RemoveUserButton';
import { TableActions } from '../KeywordsTable/TableActions';
import { CoreTable } from '../CoreTable/CoreTable';
import { FetcherWithComponents } from 'react-router-dom';
import { OutlinedButton } from '@/components/layouts/Buttons/OutlinedButton/OutlinedButton';
import { resendInvitationEmailCloudFunction } from '@/firebase/init';
import { Pagination } from '../Pagination/Pagination';
import { SearchBar } from '@/components/layouts/Inputs/SearchBar/SearchBar';
import { toastPromise } from '@/utils/toasts';

export interface TableUser {
  id: string;
  email: string;
  role: UserRole | string;
  hash?: string;
  name: string;
  isInvitation: boolean;
}
interface Props {
  users: TableUser[];
  updateUserRole: (role: UserRole, rowId: string) => void;
  handleUserDelete: (rowId: string) => void;
  extraActions?: JSX.Element;
  isLoading: boolean;
  fetcher: FetcherWithComponents<FetcherEmptyResponse>;
  onDeleteSelected: (rowSelection: RowSelectionState) => void;
  entityType: 'organization' | 'workspace';
}

const columnHelper = createColumnHelper<TableUser>();

const ITEMS_PER_PAGE = 10;

function getDropdownItems(rowId: string) {
  return userRoles.map((role) => {
    const displayRole = role.replace('_', ' ').toUpperCase();
    return (
      <DropdownItem
        key={`${rowId}-${role}`}
        data-testid={`select-item-${rowId}-${role}`}
        value={role}
        text={displayRole}
      />
    );
  });
}

export const UsersTable: FC<Props> = ({
  users,
  updateUserRole,
  handleUserDelete,
  isLoading,
  extraActions,
  onDeleteSelected,
  entityType,
}) => {
  const userId = useUserPermissionsStore((state) => state.userId);
  const userRole = useUserPermissionsStore((state) => state.workspacePermission?.role);
  const [rowSelection, setRowSelection] = useState({});
  const [resend, setResend] = useState<Record<string, boolean>>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: ITEMS_PER_PAGE, //default page size
  });
  const [searchPhrase, setSearchPhrase] = useState('');

  const displayUsers = useMemo(
    () =>
      searchPhrase.length === 0
        ? users
        : users.filter(
            (user) =>
              user.name.toLowerCase().includes(searchPhrase.toLowerCase()) ||
              user.email.toLowerCase().includes(searchPhrase.toLowerCase()),
          ),
    [searchPhrase, users],
  );

  const pageCount = Math.ceil(displayUsers.length / ITEMS_PER_PAGE);

  useEffect(() => {
    const isNotFirstPage = pagination.pageIndex !== 0;
    const isHigherPageIndexThanMaxPages = pagination.pageIndex >= pageCount;
    if (isHigherPageIndexThanMaxPages && isNotFirstPage) {
      const maxPage = Math.max(0, pageCount - 1);
      setPagination((prev) => ({ ...prev, pageIndex: maxPage }));
    }
  }, [pageCount, pagination.pageIndex]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        size: 20,
        id: 'checkbox',
        cell: ({ row }) => {
          const isCurrentUser = userId === row.original.id;
          const isAdmin = userRole === 'admin';
          return <Checkbox disabled={isCurrentUser || !isAdmin} row={row} />;
        },
      }),
      columnHelper.accessor('email', {
        cell: (props) => <Text className={s.userEmail}>{props.getValue()}</Text>,
        header: 'Email',
      }),
      columnHelper.accessor('name', {
        cell: (props) => <Text className={s.userEmail}>{props.getValue()}</Text>,
        header: 'Name',
      }),
      columnHelper.accessor('role', {
        header: 'role',
        cell: (props) => {
          const isAdmin = userRole === 'admin';
          const isCurrentUser = userId === props.row.original.id;
          return (
            <div style={{ minWidth: '250px', width: 'fit-content' }}>
              <Text
                className={[
                  s.roleCellText,
                  isCurrentUser && isUserRole(props.getValue()) ? 'current-user' : '',
                  isAdmin && isUserRole(props.getValue()) ? 'can-edit-roles' : '',
                ].join(' ')}
              >
                {props.getValue()}
              </Text>
              {isCurrentUser || !isAdmin ? null : (
                <OutlinedSelectInput
                  testId={`role-select-${props.row.id}`}
                  onChange={async (value) => {
                    updateUserRole(value as UserRole, props.row.id);
                  }}
                  defaultValue={props.getValue()}
                  className={s.roleSelect}
                >
                  {getDropdownItems(props.row.id)}
                </OutlinedSelectInput>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        header: 'Action',
        cell: ({ row }) => {
          const isCurrentUser = userId === row.original.id;
          const isAdmin = userRole === 'admin';
          const isPendingInvitation = row.original.isInvitation;
          const isResend = !!resend[row.id];
          const shouldDisplayResendButton =
            !isCurrentUser && isAdmin && isPendingInvitation && !isResend;
          return (
            <Flex justifyContent="start" style={{ gap: '.75rem' }}>
              <OutlinedButton
                title="Resend Invitation"
                onClick={() => {
                  setResend((state) => ({ ...state, [row.id]: !isResend }));
                  toastPromise(
                    resendInvitationEmailCloudFunction({
                      hash: row.original.hash as string,
                      entityType,
                    }),
                    {
                      pending: 'Sending invitation email...',
                      success: 'Invitation email sent',
                      error: 'Failed to send invitation email',
                    },
                    { position: 'bottom-right' },
                  );
                }}
                disabled={!shouldDisplayResendButton}
                className={
                  shouldDisplayResendButton
                    ? 'visible [&_span]:visible'
                    : 'invisible [&_span]:invisible'
                }
              >
                Resend
              </OutlinedButton>
              <RemoveUserButton
                disabled={isCurrentUser || !isAdmin}
                handleUserDelete={() => handleUserDelete(row.id)}
              />
            </Flex>
          );
        },
      }),
    ],
    [entityType, handleUserDelete, resend, updateUserRole, userId, userRole],
  );

  const table = useReactTable({
    data: displayUsers,
    columns,
    state: {
      rowSelection,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => row.id !== userId,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.id,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
  });

  const handlePageClick = (event: {
    index: number | null;
    selected: number;
    nextSelectedPage: number | undefined;
    event: object;
    isPrevious: boolean;
    isNext: boolean;
    isBreak: boolean;
    isActive: boolean;
  }) => {
    if (event.isNext) {
      table.getCanNextPage() && table.nextPage();
    } else if (event.isPrevious) {
      table.getCanPreviousPage() && table.previousPage();
    } else if (typeof event.nextSelectedPage === 'number') {
      table.setPageIndex(event.nextSelectedPage);
    }
  };
  return (
    <>
      <SearchBar
        cancelButton={true}
        style={{ marginTop: '1rem' }}
        disabled={isLoading}
        placeholder="Find Account"
        onSubmit={(val) => {
          setSearchPhrase(val.trim());
        }}
      />
      <TableActions
        extraActions={extraActions}
        isLoading={isLoading}
        table={table}
        onDeleteSelected={onDeleteSelected}
      />
      <CoreTable className={s.cellsHover} table={table} />
      {pageCount > 1 ? (
        <Pagination
          onClick={handlePageClick}
          pageCount={pageCount}
          currentPage={pagination.pageIndex}
          totalPages={pageCount}
        />
      ) : null}
    </>
  );
};
